import { useAbout } from '../api'
import { useLang } from '../lang'
import { Markdown } from './Markdown'
import { StateLine } from '../design'

// The About tab. The prose is one markdown document from the station profile; rather than pour
// it into a single narrow column, we split it at each `## ` heading and lay the sections out as
// an editorial page: the FIRST section is the lead (the station's story / dedication), set apart
// and centred; the rest flow in a two-column grid so they fill the width. The bibliography is a
// separate resource on its own tab — see SourcesTab.
//
// The outward links are the one part built from CONFIG rather than prose (station.yml
// about.community): where else the station is — its Ko-fi, its Bluesky, the engine's repo — as
// one row of icon links, so they read alike instead of a repo being a footnote and a support link
// being a button. They sit directly under the lead: the dedication says what this is, and the row
// says where to follow or support it. A station configuring no links gets no row.
interface Section {
  title: string
  body: string
}

function splitSections(md: string): Section[] {
  const out: Section[] = []
  let cur: Section | null = null
  for (const line of md.replace(/\r\n/g, '\n').split('\n')) {
    if (line.startsWith('## ')) {
      if (cur) out.push(cur)
      cur = { title: line.slice(3).trim(), body: '' }
    } else if (cur) {
      cur.body += `${line}\n`
    }
  }
  if (cur) out.push(cur)
  return out
}

export function AboutTab() {
  const { data, isLoading, isError } = useAbout()
  const { lang } = useLang()

  if (isLoading || !data) {
    return <StateLine busy={!isError}>{isError ? '—' : '…'}</StateLine>
  }

  const prose = (lang === 'ga' && data.intro.ga?.trim() ? data.intro.ga : data.intro.en) ?? ''
  const sections = splitSections(prose)
  const [lead, ...rest] = sections

  return (
    <section className="ed-about">
      {lead && (
        <div className="ed-about-lead">
          <img className="ed-about-hero" src="/station/brand/mark" alt="" width={132} height={117} />
          {lead.title && <p className="ed-about-eyebrow">{lead.title}</p>}
          <Markdown source={lead.body} />
          {data.community.length > 0 && (
            <ul className="ed-about-links">
              {data.community.map((link) => (
                <li key={link.url}>
                  <a
                    className="ed-about-repo"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={link.note ?? undefined}
                  >
                    <i className={`ti ${link.icon}`} aria-hidden="true" /> {link.label}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {rest.length > 0 && (
        <div className="ed-about-grid">
          {rest.map((s) => (
            <section className="ed-about-cell" key={s.title}>
              <h2>{s.title}</h2>
              <Markdown source={s.body} />
            </section>
          ))}
        </div>
      )}
    </section>
  )
}
